import { makeObservable, observable } from "mobx";
import { EngagementsApi } from "../libs/api";
import { ILoanCalculationContext } from "../libs/models/Engagements";
import { ILoanPaymentInformation } from "../libs/models/LoanOverpaymentCalculation/LoanPaymentInformation";
import { calculateNewLoanTerm, calculateNewMaturityDate, getLoanCostCalculation } from "../libs/utils";
import { ILoanExtraPaymentCalculation } from "../libs/models/Offer/Offer";
import { TelemetryStore } from "./TelemetryStore";
import { OverpaymentsEvents } from "../libs/models/Telemetry/OverpaymentCalculationEvents";

export class CalculationsStore {
  private engagementsApi: EngagementsApi;

  private telemetry: TelemetryStore;

  @observable
  calculationStatus: "loading" | "loaded" | "error" = "loading";

  @observable
  calculationTarget: "monthlyPayment" | "numberOfInstalments" = "monthlyPayment";

  @observable
  calculationContext?: ILoanCalculationContext;

  @observable
  initialLoanPaymentInformation?: ILoanPaymentInformation;

  @observable
  postCalculationPaymentInformation?: ILoanPaymentInformation;

  @observable
  extraPayment: number = 0;

  @observable
  accountNumber = "";

  constructor(engagementsApi: EngagementsApi, telemetryStore: TelemetryStore) {
    this.telemetry = telemetryStore;
    this.engagementsApi = engagementsApi;
    makeObservable(this);
  }

  initializeCalculator = async (accountNumber: string) => {
    this.extraPayment = 0;
    this.calculationTarget = "monthlyPayment";
    this.accountNumber = accountNumber;

    this.calculationStatus = "loading";
    const response = await this.engagementsApi.getLoanDetails(accountNumber);
    if (!response?.ok || response.data == null) {
      this.calculationStatus = "error";
      return;
    }
    this.calculationContext = response.data;

    const loanCostData = getLoanCostCalculation(
      this.calculationContext.interestRate ?? 0,
      this.calculationContext.numberOfTermsLeft - 1 ?? 0,
      this.calculationContext.balance ?? 0,
      this.calculationContext.fees,
      0,
      this.calculationContext.insurancePercentage
    );

    this.initialLoanPaymentInformation = {
      monthlyPayment: loanCostData.monthlyPayment,
      numberOfInstalments: this.calculationContext.numberOfTermsLeft,
      loanAmount: this.calculationContext.balance,
      interestRate: this.calculationContext.interestRate,
      dateFullyRepaid: calculateNewMaturityDate(this.calculationContext.maturityDate, 0, 0),
      creditCostPerMonth: loanCostData.creditCostPerMonth,
      totalCreditCost: loanCostData.totalCreditCost,
      insuranceCost: loanCostData.insuranceCost,
    };

    this.postCalculationPaymentInformation = { ...this.initialLoanPaymentInformation };

    this.calculationStatus = "loaded";
  };

  setExtraPayment = (amount: number) => {
    this.extraPayment = amount;
  };

  setCalculationTarget = (target: "monthlyPayment" | "numberOfInstalments") => {
    this.calculationTarget = target;
  };

  refreshPaymentInformation = () => {
    if (this.calculationContext == null || this.initialLoanPaymentInformation == null) {
      return;
    }

    if (this.extraPayment === 0) {
      this.postCalculationPaymentInformation = { ...this.initialLoanPaymentInformation };
      return;
    }

    let loanAfterOverpayment: ILoanExtraPaymentCalculation;
    if (this.calculationTarget === "monthlyPayment") {
      loanAfterOverpayment = getLoanCostCalculation(
        this.calculationContext.interestRate,
        this.calculationContext.numberOfTermsLeft - 1,
        this.calculationContext.balance - this.extraPayment,
        this.calculationContext.fees,
        0,
        this.calculationContext.insurancePercentage
      );

      this.postCalculationPaymentInformation = {
        ...this.initialLoanPaymentInformation,
        monthlyPayment: loanAfterOverpayment.monthlyPayment,
      };
    } else {
      const newLoanTermCount = calculateNewLoanTerm(
        this.calculationContext.balance,
        this.calculationContext.interestRate,
        this.initialLoanPaymentInformation.monthlyPayment - this.initialLoanPaymentInformation.insuranceCost,
        this.extraPayment
      );

      loanAfterOverpayment = getLoanCostCalculation(
        this.calculationContext.interestRate ?? 0,
        newLoanTermCount - 1,
        this.calculationContext.balance - this.extraPayment,
        this.calculationContext.fees,
        0,
        this.calculationContext.insurancePercentage
      );

      let newMaturityDate = calculateNewMaturityDate(
        this.calculationContext.maturityDate,
        this.calculationContext.numberOfTermsLeft,
        newLoanTermCount
      );

      if (newMaturityDate > this.initialLoanPaymentInformation.dateFullyRepaid) {
        newMaturityDate = this.initialLoanPaymentInformation.dateFullyRepaid;
      }

      this.postCalculationPaymentInformation = {
        ...this.initialLoanPaymentInformation,
        numberOfInstalments: newLoanTermCount,
        dateFullyRepaid: newMaturityDate,
      };
    }

    this.postCalculationPaymentInformation = {
      ...this.postCalculationPaymentInformation,
      loanAmount: loanAfterOverpayment.loanAmount,
      creditCostPerMonth: Math.round(
        loanAfterOverpayment.totalCreditCost / this.postCalculationPaymentInformation.numberOfInstalments
      ),
      totalCreditCost: loanAfterOverpayment.totalCreditCost,
    };
  };

  runSelfTest = (failThreshold: number) => {
    if (this.initialLoanPaymentInformation == null || this.calculationContext == null) {
      return;
    }

    const calculatedMonthlyPayment = this.initialLoanPaymentInformation.monthlyPayment;
    const realMonthlyPayment = this.calculationContext.totalTermPayment;

    const difference = Math.abs(calculatedMonthlyPayment - realMonthlyPayment);
    const threshold = (realMonthlyPayment * failThreshold) / 100;
    if (difference > threshold) {
      this.telemetry.trackEvent(OverpaymentsEvents.SelfTestFailed, {
        accountNumber: this.accountNumber,
        calculatedMonthlyPayment,
        realMonthlyPayment,
      });
    }
  };
}
